import React from 'react';
import styled from 'styled-components';
import {ButtonDropdownField, DropdownList, tokensForThemeProvider} from '@forecast-it/design-system';
import {FormattedMessage} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {findRelativeDatePreset, RELATIVE_DATE_PRESETS} from '../relativeDatePresets';

const tokens = tokensForThemeProvider.sky.sys;

const PLACEHOLDER_MESSAGE_ID = 'ai_report_builder.date_preset_placeholder';
const CUSTOM_RANGE_MESSAGE_ID = 'ai_report_builder.date_preset_custom';

/* the design: this field carries the same 8px corner radius as the adjacent date-range
 * field (sky/sys/size/borderradius/3) — ButtonDropdownField's own default (borderradius/2, 4px) is
 * a shade tighter, so it's overridden here rather than forked. */
const RoundedButtonDropdownField = styled(ButtonDropdownField)`
	border-radius: ${tokens.size.borderradius['3']};
`;

/** The "Select Date Range" dropdown of relative period presets, sitting immediately to the left
 * of the absolute DateRangePicker in the toolbar. Selecting a preset resolves it and writes the
 * result into the shared period state; the adjacent picker then shows the resolved dates.
 * Operates purely through useReportBuilder()'s period/setPeriodPreset - it holds no local state
 * of its own. */
export function DateRangePresetDropdown(): JSX.Element {
	const {period, setPeriodPreset, setPeriod} = useReportBuilder();
	const selected = findRelativeDatePreset(period.datePreset);

	// Detaches from the current preset without changing the dates the picker shows - the escape
	// hatch for "freeze exactly this range." Re-selecting the same dates via the date picker itself
	// wouldn't reliably fire a change (no-op if the value doesn't move), so this is the only direct
	// way to stop a period from rolling forward while keeping what it last resolved to.
	const clearToCustomRange = (): void => {
		setPeriod(period.startDate, period.endDate);
	};

	return (
		<DropdownList>
			<DropdownList.Trigger>
				{/* the design: the placeholder reads in the same dark main colour as every
				 * other toolbar label, not muted grey — isDefaultLabel is left off (it only affects
				 * text colour, per ButtonDropdownField's own styles) so the placeholder state never
				 * dims. */}
				<RoundedButtonDropdownField width={'auto'} data-cy={'ai-report-date-preset'}>
					<FormattedMessage id={selected?.labelId ?? PLACEHOLDER_MESSAGE_ID} />
				</RoundedButtonDropdownField>
			</DropdownList.Trigger>
			{/* align="start" (DropdownList.Content defaults to "end"): this dropdown is the leftmost
			 * control on the page, so an end-aligned menu would extend leftward past the trigger and
			 * off the edge of the viewport instead of into the open space to its right. */}
			<DropdownList.Content offset={4} align={'start'}>
				<DropdownList.Item onClick={clearToCustomRange} data-cy={'ai-report-date-preset-custom'}>
					<FormattedMessage id={CUSTOM_RANGE_MESSAGE_ID} />
				</DropdownList.Item>
				{RELATIVE_DATE_PRESETS.map(preset => (
					<DropdownList.Item
						key={preset.token}
						onClick={() => setPeriodPreset(preset.token)}
						data-cy={`ai-report-date-preset-option-${preset.token}`}
					>
						<FormattedMessage id={preset.labelId} />
					</DropdownList.Item>
				))}
			</DropdownList.Content>
		</DropdownList>
	);
}
